import * as React from 'react';
import { Link } from 'react-router-dom';
import Page from '../../components/Page';
import {
  Order,
  LineItem,
  Shipment,
  AttributeChoice,
  Address,
  Transaction,
  Fulfillment,
} from '../../order';
import { ProfileContext, ProfileManager } from '../../profile';
import * as dateFormat from 'dateformat';
import Section from '../../components/Section';
import Loader from '../../components/Loader';
import { useRequest, getHeaders } from '../../api/client';
import Flash, { ErrorFlash } from '../../components/Flash';
import { classes } from '../../components/Button';
import { Contract } from '../../contract';

type Props = {
  order: Order;
};

type OrderEvent = {
  label: string;
  date: string;
};

const DATE_FORMAT = 'd mmmm, yyyy - HH:MM:s';

const getPrice = (value: number, currency: string) => (
  <React.Fragment>
    {currency} {(value / 100).toFixed(2)}
  </React.Fragment>
);

const getEvents = (order: Order) => {
  const items: OrderEvent[] = order.transactions.map(
    (item: Transaction): OrderEvent => ({
      label: `Transaction: ${item.type} of amount: ${item.amount_currency} ${(
        item.amount / 100
      ).toFixed(2)}`,
      date: dateFormat(item.created_at, DATE_FORMAT),
    }),
  );

  return items;
};

const calculateSalePrice = (value: number, contract: Contract) => {
  if (!contract || !contract.is_active || contract.type === 'monthly_fee') {
    return value;
  }

  if (contract.amount > 0) {
    return value - contract.amount;
  }

  if (contract.percentage > 0) {
    return value - value * (contract.percentage / 100);
  }

  return value;
};

const renderAddress = (address: Address) => (
  <React.Fragment>
    <dl className="f6 lh-copy mv2">
      <dt className="dib gray w-third">Name</dt>
      <dd className="dib ml0">
        {address.first_name} {address.last_name}
      </dd>
    </dl>
    <dl className="f6 lh-copy mv2">
      <dt className="dib gray w-third">
        Address:
        <br />
        <br />
      </dt>
      <dd className="dib ml0">
        {address.line_1}
        <br />
        {address.line_2}
        <br />
        {address.line_3}
      </dd>
    </dl>
    <dl className="f6 lh-copy mv2">
      <dt className="dib gray w-third">City:</dt>
      <dd className="dib ml0">{address.city}</dd>
    </dl>
    <dl className="f6 lh-copy mv2">
      <dt className="dib gray w-third">Country:</dt>
      <dd className="dib ml0">{address.country}</dd>
    </dl>
    <dl className="f6 lh-copy mv2">
      <dt className="dib gray w-third">Postal Code:</dt>
      <dd className="dib ml0">{address.postal_code}</dd>
    </dl>
  </React.Fragment>
);

const user = (context: ProfileManager, id: string) => {
  const [state, setState] = useRequest(() => ({
    url: `/users/${id}`,
    method: 'GET',
    headers: getHeaders(context),
  }));

  return state;
};

const EmailCustomerLink = (props: any) => {
  const context = React.useContext(ProfileContext);
  const state = user(context, props.order.user_id);

  if (state.loading || !state.data) {
    return <Loader />;
  }

  if (state.error) {
    return <ErrorFlash error={state.error} />;
  }

  return (
    <a
      href={`mailto:${state.data.email}?subject=Your Recent Order (#${props.order.order_number})`}
      className="link underline b black"
    >
      Contact Customer
    </a>
  );
};

const isFulfilled = (order: Order) => {
  return order.shipments[0].fulfillment !== null;
};

const canFulfill = (order: Order) =>
  order.status !== 'unpaid' &&
  order.shipments[0].status !== 'delivered' &&
  order.shipments[0].status !== 'cancelled' &&
  order.shipments[0].fulfillment === null;

const canCancel = (order: Order) =>
  order.status === 'unpaid' || order.status === 'paid';

const canHold = (order: Order) => order.status === 'paid';

export default function Order(props: Props) {
  const context = React.useContext(ProfileContext);
  const contract = context.profile.company.contract;
  const [order, _] = React.useState(props.order);

  return (
    <Page
      title="Order Details"
      subtitle={`#${order.order_number} — Placed on ${dateFormat(
        order.created_at,
        DATE_FORMAT,
      )}`}
    >
      <div className="flex justify-center ml-auto mr-auto">
        {canFulfill(order) ? (
          <Link
            to={{
              pathname: `/orders/${order.id}/shipments/${order.shipments[0].id}/ship`,
              state: {
                order,
              },
            }}
            className={`${classes} dib mb2`}
          >
            Mark as shipped
          </Link>
        ) : null}
        {canHold(order) ? (
          <Link
            to={{
              pathname: `/orders/${order.id}/shipments/${order.shipments[0].id}/hold`,
              state: {
                order,
              },
            }}
            className={`${classes} blue dib mb2`}
          >
            Put on Hold
          </Link>
        ) : null}
        {canCancel(order) ? (
          <Link
            to={{
              pathname: `/orders/${order.id}/shipments/${order.shipments[0].id}/cancel`,
              state: {
                order,
              },
            }}
            className={`${classes} red dib mb2`}
          >
            Cancel Order
          </Link>
        ) : null}
      </div>
      {order.shipments.map((shipment: Shipment) =>
        shipment.cancelled_at ? (
          <Flash
            text={`Order Cancelled on: ${dateFormat(
              shipment.cancelled_at,
              DATE_FORMAT,
            )}. Reason: ${shipment.cancelled_notes}`}
            color="red"
          />
        ) : null,
      )}
      {order.shipments.map((shipment: Shipment) =>
        shipment.on_hold_until ? (
          <Flash
            text={`On hold until: ${dateFormat(
              shipment.on_hold_until,
              DATE_FORMAT,
            )}. Reason: ${shipment.on_hold_notes}`}
            color="blue"
          />
        ) : null,
      )}
      {isFulfilled(order) ? (
        <Section title="Fulfillment details">
          <table className="w-100 collapse tl lh-copy">
            <thead className="black-50 f6 ttu">
              <tr>
                <th className="pv3 bb b--black-10">Carrier</th>
                <th className="pv3 bb b--black-10">Tracking</th>
                <th className="pv3 bb b--black-10">Notes</th>
                <th className="pv3 bb b--black-10 w4">Price</th>
                <th className="pv3 bb b--black-10 w5 tr">Date</th>
              </tr>
            </thead>
            <tbody>
              {order.shipments
                .map((shipment: Shipment) => shipment.fulfillment)
                .filter((fulfillment: Fulfillment) => fulfillment)
                .map((fulfillment: Fulfillment) => (
                  <tr>
                    <td className="pv3">{fulfillment.carrier_name}</td>
                    <td className="pv3">
                      {fulfillment.tracking_url ? (
                        <a
                          href={fulfillment.tracking_url}
                          target="_blank"
                          className="link underline b black"
                        >
                          {fulfillment.tracking_code}
                        </a>
                      ) : (
                        fulfillment.tracking_code
                      )}
                    </td>
                    <td className="pv3">{fulfillment.notes}</td>
                    <td className="pv3">
                      {getPrice(fulfillment.price, fulfillment.price_currency)}
                    </td>
                    <td className="pv3 tr">
                      {dateFormat(fulfillment.created_at, DATE_FORMAT)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </Section>
      ) : null}
      {order.shipments.map((shipment: Shipment) => (
        <Section title="Overview">
          <table className="w-100 collapse tl lh-copy">
            <thead className="black-50 f6 ttu">
              <tr>
                <th className="pv3 tl">Product</th>
                <th className="pv3 tl">Variant</th>
                <th className="pv3 tl">Attributes</th>
                <th className="pv3 tc">Unit Price</th>
                <th className="pv3 tc w2 black-50" />
                <th className="pv3 tc">Quantity</th>
                <th className="pv3 tc w2 black-50" />
                <th className="pv3 tr">Total Price</th>
              </tr>
            </thead>
            <tbody>
              {shipment.line_items.map((lineItem: LineItem) => (
                <tr>
                  <td className="bt b--black-10 pv2">{lineItem.name}</td>
                  <td className="bt b--black-10 pv2">
                    {lineItem.variant_name}
                  </td>
                  <td className="bt b--black-10 pv2">
                    {lineItem.selected_attributes.map(
                      (choice: AttributeChoice) => (
                        <p className="ma0 lh-copy">
                          {choice.name} –{' '}
                          <strong className="black">{choice.option}</strong>
                        </p>
                      ),
                    )}
                  </td>
                  <td className="bt b--black-10 pv2 tc">
                    {getPrice(lineItem.price.value, lineItem.price.currency)}
                  </td>
                  <td className="bt b--black-10 tc black-50">✕</td>
                  <td className="bt b--black-10 pv2 tc">{lineItem.quantity}</td>
                  <td className="bt b--black-10 tc black-50">=</td>
                  <td className="bt b--black-10 pv2 tr">
                    {getPrice(
                      lineItem.price.value * lineItem.quantity,
                      lineItem.price.currency,
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>&nbsp;</td>
                <th className="bt b--black-10 pv3 tc black-50">Subtotal</th>
                <td className="bt b--black-10 pv3 tr" colSpan={4}>
                  {getPrice(
                    shipment.pricing.subtotal,
                    shipment.pricing.currency,
                  )}
                </td>
              </tr>
              <tr>
                <td colSpan={3}>&nbsp;</td>
                <th className="bt b--black-10 pv3 tc black-50">Shipping</th>
                <td className="bt b--black-10 pv3 tr" colSpan={4}>
                  {getPrice(
                    shipment.pricing.shipping,
                    shipment.pricing.currency,
                  )}
                </td>
              </tr>
              <tr>
                <td colSpan={3}>&nbsp;</td>
                <th className="bt b--black-10 pv3 tc b black">Paid Total</th>
                <td className="bt b--black-10 pv3 tr" colSpan={4}>
                  {getPrice(
                    shipment.pricing.shipping + shipment.pricing.subtotal,
                    shipment.pricing.currency,
                  )}
                </td>
              </tr>
              <tr>
                <td colSpan={3}>&nbsp;</td>
                <th className="bt b--black-10 pv3 tc b green">
                  Proceeds Total
                </th>
                <td className="bt b--black-10 pv3 tr b green" colSpan={4}>
                  {getPrice(
                    calculateSalePrice(shipment.pricing.subtotal, contract) +
                      shipment.pricing.shipping,
                    shipment.pricing.currency,
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </Section>
      ))}
      <Section title="Shipping Info">
        <table className="w-100 collapse tl lh-copy">
          <thead className="black-50 f6 ttu">
            <tr>
              <th className="pv3 bb b--black-10">Name</th>
              <th className="pv3 bb b--black-10">Email</th>
              <th className="pv3 bb b--black-10">Billing</th>
              <th className="pv3 bb b--black-10">Shipping</th>
            </tr>
          </thead>
          <tbody>
            <td className="pv3">
              {order.billing_address.first_name}{' '}
              {order.billing_address.last_name}
            </td>
            <td className="pv3">
              <EmailCustomerLink id={order.user_id} order={order} />
            </td>
            <td className="pv3">{renderAddress(order.billing_address)}</td>
            <td className="pv3">{renderAddress(order.shipping_address)}</td>
          </tbody>
        </table>
      </Section>
      {context.profile.type === 'admin' && (
        <Section title="Order History">
          <table className="w-100 collapse tl lh-copy">
            <thead className="black-50 f6 ttu">
              <tr>
                <th className="w-25 pv3 bb b--black-10">Date</th>
                <th className="w-75 pv3 bb b--black-10">Name</th>
              </tr>
            </thead>
            <tbody>
              {getEvents(order).map((event: OrderEvent) => (
                <tr>
                  <td className="pv3">{event.date}</td>
                  <td className="pv3">{event.label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </Page>
  );
}
